import { Component, output } from '@angular/core';
import {ReactiveFormsModule, FormBuilder, Validators, FormGroup} from '@angular/forms';
import { PhoneInput } from '../../parts/phone-input/phone-input.component';
import { InputLabel } from '../../parts/input-label/input-label.component';
import { EmailInput } from '../../parts/email-input/email-input.component';
import { UserService } from '../../core/services/user.service';
import { CustomValidators } from '../../core/validators/custom-validators';
import { ApiResponseUtilities } from '../../core/utilities/api-response.utilities';
import { CreateUserRequest } from '../../core/models/data/user.model';
import { RandomUtilities } from '../../core/utilities/random.utilities';
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { UsersMessagingConstants } from '../../core/constants/messaging.constants';
import { APISanitizerUtilities } from '../../core/utilities/api-sanitizer.utilities';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';

@Component({
  selector: 'ms-add-user-form',
  imports: [PhoneInput, EmailInput, InputLabel, ReactiveFormsModule],
  templateUrl: './add-user.form.html',
})
export class AddUserForm extends BaseFormComponent {

  public addUserForm: FormGroup;
  
  public cancelRequested = output();
  public submissionResult = output<{ success: boolean; message: string }>();

  constructor(private userService: UserService, private fb: FormBuilder) {
    super();
    this.addUserForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, CustomValidators.email]],
      telephone: [{}, [CustomValidators.phone]]
    });
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public async onSubmit(): Promise<void> {
    if (this.addUserForm.invalid) {
      this.markAllFieldsAsTouched();
    } else {
      this.isSubmitting = true;
      const createUser = {
        firstName: this.addUserForm.value.firstName,
        lastName: this.addUserForm.value.lastName,
        emailAddress: this.addUserForm.value.email,
        phoneCountryCode: this.addUserForm.value.telephone?.countryCode,
        phoneNumber: this.addUserForm.value.telephone?.phoneNumber,
        phoneExtension: this.addUserForm.value.telephone?.extension,
        avatarHexColor: RandomUtilities.getRandomAvatarColor()
      } as CreateUserRequest;

      let submitResponse;
      let message;

      try {
        const sanitizedPayload = APISanitizerUtilities.sanitizePayload(
          this.setPhonePropertiesToNullIfEmpty(createUser)
        );

        submitResponse = await this.userService.createUser(sanitizedPayload);

        if (submitResponse.success === false && ApiResponseUtilities.isConflictStatusCode(submitResponse.statusCode)) {
            message = UsersMessagingConstants.USERS_ERROR_EMAIL_IN_USE();
            this.addUserForm.controls['email'].setErrors({ email: { message: message } });
        } else {
          message =  UsersMessagingConstants.USERS_ADD_SUCCESS();
        }
      } catch (error) {
        console.error('Submission error:', error);
      } finally {
        this.isSubmitting = false;
        
        this.submissionResult.emit({ success: submitResponse?.success!, message: message!});        
      }
    }
  }

  public resetForm(): void {
    this.addUserForm.reset();
  }

  private markAllFieldsAsTouched(): void {
    this.addUserForm.markAllAsTouched();
  }

  public getFormState(): any {
    return {
      formValue: this.addUserForm.value,
      formValid: this.addUserForm.valid,
      formTouched: this.addUserForm.touched,
      formDirty: this.addUserForm.dirty,
      emailErrors: this.addUserForm.get('email')?.errors,
      telephoneErrors: this.addUserForm.get('telephone')?.errors
    };
  }

  private setPhonePropertiesToNullIfEmpty(user: CreateUserRequest): CreateUserRequest {
    if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(user.phoneCountryCode)) {
      user.phoneCountryCode = null;
    }
    if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(user.phoneNumber)) {
      user.phoneCountryCode = null;
      user.phoneNumber = null;
    }
    if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(user.phoneExtension)) {
      user.phoneExtension = null;
    }

    return user;
  }
}
